import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Input } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ReactComponent as Paperclip } from 'Assets/controls/paperclip.svg';
import { ReactComponent as Send } from 'Assets/controls/send.svg';
import { selectCurrentUser } from 'Redux/User/slice';
import { useAppDispatch } from 'Redux/store';
import { getCurrentUser } from 'Redux/User/reducer';
import { MessageContainer } from './MessageContainer/MessageContainer';
import { TextMessage } from './TextMessage/TextMessage';
import { DataMessage } from './DataMessage/DataMessage';
import { MessageSeparator } from './MessageSeparator/MessageSeparator';
import { ITextMessage, IDataMessage } from 'Shared/Types/chat';
import styles from './MessagesList.module.scss';

type MessagesListProps = {
  messages: (ITextMessage | IDataMessage)[];
  isGroup: boolean;
};

// Type guards
const isTextMessage = (
  message: ITextMessage | IDataMessage,
): message is ITextMessage => {
  return (message as ITextMessage).text !== undefined;
};

const isDataMessage = (
  message: ITextMessage | IDataMessage,
): message is IDataMessage => {
  return (
    (message as IDataMessage).link !== undefined &&
    (message as IDataMessage).file_size !== undefined
  );
};

type FileData = {
  file: File;
  name: string;
  extension: string;
  sizeInBytes: number;
};

export const MessagesList: React.FC<MessagesListProps> = ({
  messages,
  isGroup,
}) => {
  const dispatch = useAppDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const [messageElements, setMessageElements] = useState<JSX.Element[]>([]);
  const [message, setMessage] = useState('');
  const [fileData, setFileData] = useState<FileData | null>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);

  const checkDifferentDays = (start: string, end: string): boolean => {
    return start.split('T')[0] !== end.split('T')[0];
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleFileDeletion = () => {
    setMessage('');
    setFileData(null);
  };

  const handleSendMessage = () => {
    console.log('Message =>', message);
    console.log('Message has been sent');
    setMessage('');
    setFileData(null);

    // send the file to the server

    // const formData = new FormData();
    // formData.append('file', fileData.file);
    // await dispatch(someSend(formData));

    // alternative way to send the file

    // const reader = new FileReader();
    // reader.onload = async () => {
    //   const fileBase64 = reader.result?.toString().split(',')[1];
    //   console.log('File Base64:', fileBase64);
    //   if (fileBase64) {
    //     await dispatch(someSend({ file: fileBase64 }));
    //   }
    // };
    // reader.readAsDataURL(fileData.file);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const maxNameLength = 50;
      const nameParts = file.name.split('.');
      const extension = nameParts.pop() || '';
      let name = nameParts.join('.');
      const sizeInBytes = file.size;

      setFileData({ file, name, extension, sizeInBytes });

      if (name.length > maxNameLength) {
        name = `${name.substring(0, maxNameLength)}...`;
      }
      setMessage(`File: ${name}.${extension}`);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      dispatch(getCurrentUser());
      return;
    }

    const generatedElements = messages.map((message, index) => {
      let relevantMessage;
      let messageSeparatorIsRequired =
        index === 0 ||
        checkDifferentDays(
          messages[index - 1].timestamp,
          messages[index].timestamp,
        );

      if (isTextMessage(message)) {
        relevantMessage = <TextMessage key={message.id} message={message} />;
      } else if (isDataMessage(message)) {
        relevantMessage = <DataMessage key={message.id} message={message} />;
      }

      return (
        <React.Fragment key={`fragment-${message.id}`}>
          {messageSeparatorIsRequired && (
            <MessageSeparator
              key={`separator-${message.id}`}
              timestamp={message.timestamp}
            />
          )}
          <MessageContainer
            key={`container-${message.id}`}
            message={message}
            isGroup={isGroup}
          >
            {relevantMessage}
          </MessageContainer>
        </React.Fragment>
      );
    });

    setMessageElements(generatedElements);
  }, [currentUser, messages, dispatch]);

  useEffect(() => {
    const messagesContainer = messagesListRef.current;
    if (!messagesContainer) return;

    messagesContainer.setAttribute('data-old-scroll', '0');

    const scrollDown = (container: HTMLDivElement) => {
      const scrollID = setInterval(() => {
        container.scrollBy(0, 90);

        const oldScroll = parseFloat(
          container.getAttribute('data-old-scroll') || '0',
        );
        const stopScroll =
          container.scrollTop > 0 && oldScroll === container.scrollTop;

        if (stopScroll) {
          clearInterval(scrollID);
        } else {
          container.setAttribute(
            'data-old-scroll',
            container.scrollTop.toString(),
          );
        }
      }, 100);
    };

    scrollDown(messagesContainer);
  }, [messageElements]);

  return (
    <div className={styles.scrollbarWrapper}>
      <div className={styles.messagesList} ref={messagesListRef}>
        {messageElements}
      </div>
      <div className={styles.messagesList__input}>
        <Input.TextArea
          className={`${styles.messagesList__input__field} ${
            fileData !== null && styles.messagesList__input__field_file
          }`}
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder="Start typing here..."
          disabled={fileData !== null}
          onChange={handleTextAreaChange}
          value={message}
        />
        {fileData === null && (
          <label>
            <Paperclip className={styles.messagesList__input__uploadButton} />
            <input
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </label>
        )}
        {fileData !== null && (
          <CloseOutlined
            className={styles.messagesList__input__deleteButton}
            onClick={handleFileDeletion}
          />
        )}
        <Send
          className={styles.messagesList__input__sendButton}
          onClick={handleSendMessage}
        />
      </div>
    </div>
  );
};
